/**
 * Prints a stock document (GRN, Site DN, transfer…) as a proper one-page document in its own window
 * — not the whole app page (Movements idea 3, Record movement idea 6).
 */
import { formatDate, formatDateTime } from '@/lib/format';
import { formatQty } from './inventoryModel';

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const TITLES = {
  GRN: 'Goods Received Note',
  'site-dn': 'Delivery Note',
  transfer: 'Stock Transfer',
  testing: 'Lab Use Note',
  return: 'Return to Stock',
  adjustment: 'Stock Adjustment'
};

const SAFE_IMAGE = /^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/;

/** The "Received by" block: the site's name, time and signature once the DN was confirmed (21 #9). */
function receivedBlock(receipt) {
  if (!receipt) return 'Received by — name, signature, date';
  const image = SAFE_IMAGE.test(String(receipt.signature || '')) ? `<img class="sig" src="${receipt.signature}" alt="Signature">` : '';
  return `${image}Received by <b>${escapeHtml(receipt.name || '—')}</b>${receipt.at ? ` · ${escapeHtml(formatDateTime(receipt.at))}` : ''}${receipt.notes ? `<br><span class="muted">${escapeHtml(receipt.notes)}</span>` : ''}`;
}

/**
 * doc = { kind, label, date, from, to, project, reference, by, notes, lines: [{ part_number, description, qty }],
 *         receipt?: { name, at, notes, signature (data:image/png;base64,…) } }
 * Returns the HTML (exported for tests).
 */
export function documentHtml(doc) {
  const title = TITLES[doc.kind] || 'Stock Document';
  const rows = (doc.lines || []).map((line, index) => `
      <tr>
        <td class="n">${index + 1}</td>
        <td class="mono">${escapeHtml(line.part_number || '—')}</td>
        <td>${escapeHtml(line.description || '')}</td>
        <td class="n">${escapeHtml(formatQty(Math.abs(Number(line.qty) || 0)))}</td>
      </tr>`).join('');
  const total = (doc.lines || []).reduce((sum, line) => sum + Math.abs(Number(line.qty) || 0), 0);
  const facts = [
    ['Date', formatDate(doc.date)],
    ['Reference', doc.label || doc.reference || '—'],
    ['From', doc.from || '—'],
    ['To', doc.to || '—'],
    doc.project ? ['Project', doc.project] : null,
    doc.po ? ['Purchase order', doc.po] : null,
    ['Recorded by', doc.by || '—']
  ].filter(Boolean);
  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><title>${escapeHtml(`${title} ${doc.label || ''}`.trim())}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #1c1d3a; margin: 32px; font-size: 13px; }
  header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #1c1d5e; padding-bottom: 12px; }
  h1 { font-size: 22px; margin: 0 0 4px; }
  .brand { font-weight: 800; font-size: 18px; color: #1c1d5e; }
  .muted { color: #5c5e80; }
  dl { display: grid; grid-template-columns: 140px 1fr 140px 1fr; gap: 6px 12px; margin: 18px 0; }
  dt { color: #5c5e80; } dd { margin: 0; font-weight: 600; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  th, td { border-bottom: 1px solid #d9dbe8; padding: 7px 8px; text-align: left; }
  th { background: #f3f4f9; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; }
  .n { text-align: right; } .mono { font-family: 'Courier New', monospace; }
  tfoot td { font-weight: 700; border-bottom: none; }
  .notes { margin-top: 14px; }
  .sign { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-top: 56px; }
  .sign > div { border-top: 1px solid #1c1d3a; padding-top: 6px; }
  .sign .sig { display: block; max-height: 64px; max-width: 240px; margin: -70px 0 6px; }
  @media print { body { margin: 14mm; } }
</style></head>
<body>
  <header>
    <div><h1>${escapeHtml(title)}</h1><div class="muted">${escapeHtml(doc.label || '')}</div></div>
    <div style="text-align:right"><div class="brand">Sara</div><div class="muted">Printed ${escapeHtml(formatDateTime(new Date().toISOString()))}</div></div>
  </header>
  <dl>${facts.map(([label, value]) => `<dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd>`).join('')}</dl>
  <table>
    <thead><tr><th class="n">#</th><th>Part no.</th><th>Description</th><th class="n">Qty</th></tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr><td></td><td colspan="2">${(doc.lines || []).length} line(s)</td><td class="n">${escapeHtml(formatQty(total))}</td></tr></tfoot>
  </table>
  ${doc.notes ? `<p class="notes"><b>Notes:</b> ${escapeHtml(doc.notes)}</p>` : ''}
  <div class="sign"><div>Delivered by — name, signature, date</div><div>${receivedBlock(doc.receipt)}</div></div>
</body></html>`;
}

/** Opens the document in a new window and prints it. Returns false when pop-ups are blocked. */
export function printStockDocument(doc) {
  const win = typeof window !== 'undefined' ? window.open('', '_blank', 'width=900,height=1100') : null;
  if (!win) return false;
  win.document.open();
  win.document.write(documentHtml(doc));
  win.document.close();
  win.focus();
  setTimeout(() => {
    try { win.print(); } catch (err) { /* the user can still print from the window */ }
  }, 250);
  return true;
}

/**
 * Like printStockDocument, for a document that needs something loaded first (the signature of a received
 * Site DN): the window opens straight away (so the pop-up is not blocked), then `prepare()` adds to `doc`.
 */
export async function printStockDocumentWhenReady(doc, prepare) {
  const win = typeof window !== 'undefined' ? window.open('', '_blank', 'width=900,height=1100') : null;
  if (!win) return false;
  win.document.open();
  win.document.write('<!doctype html><title>Preparing…</title><p style="font-family:Arial;margin:32px;color:#5c5e80">Preparing the document…</p>');
  let extra = {};
  try {
    extra = (await prepare?.()) || {};
  } catch (err) {
    extra = {};
  }
  win.document.open();
  win.document.write(documentHtml({ ...doc, ...extra }));
  win.document.close();
  win.focus();
  setTimeout(() => {
    try { win.print(); } catch (err) { /* the user can still print from the window */ }
  }, 250);
  return true;
}
